/** PROTOTYPE. Model builders for the server that serves this page and for desktop servers. */
import type {
  CmmdRuntimeDiagnosticsSnapshot,
  RuntimeDiagnosticsResponse,
} from "@cmmd-forge/contracts";

import type { DesktopDiagnosticsTarget } from "../DiagnosticsDesktopTabs.logic";
import type { DiagnosticsTargetOption } from "../DiagnosticsTargetPicker.logic";
import { formatTimestamp, shortId, state } from "./diagnosticsPrototypeFormat";
import type {
  DiagnosticsPrototypeModel,
  PrototypeEvent,
  PrototypeFact,
  PrototypeVerdict,
} from "./diagnosticsPrototypeTypes";

function serverFacts(inv: RuntimeDiagnosticsResponse): PrototypeFact[] {
  const failing = inv.tools.filter((tool) => tool.status === "error").length;
  const current = inv.currentProcess;
  return [
    {
      label: "Build",
      value: inv.runtime.sandboxRuntimeVersion ?? inv.runtime.commit ?? "not reported",
      mono: true,
    },
    { label: "Machine", value: inv.machineId, mono: true },
    {
      label: "Platform",
      value: current
        ? `${current.platform}/${current.arch}, Node ${current.nodeVersion}`
        : "not reported",
    },
    {
      label: "Uptime",
      value: current ? `${Math.floor(current.uptimeSeconds / 60)} min` : "not reported",
    },
    { label: "Processes", value: String(inv.processes.length) },
    {
      label: "Tools",
      value: failing === 0 ? `${inv.tools.length} ok` : `${failing} failing`,
      tone: failing === 0 ? "ready" : "error",
    },
  ];
}

function serverVerdict(input: {
  readonly label: string;
  readonly inv: RuntimeDiagnosticsResponse | undefined;
  readonly error: string | undefined;
}): PrototypeVerdict {
  const source = "Read from this server's own process table and tool checks.";
  if (!input.inv) {
    return {
      label: "Unknown",
      sentence: input.error ?? "The server has not returned its inventory yet.",
      tone: "muted",
      observedAt: null,
      source,
      disagreement: null,
    };
  }
  const failing = input.inv.tools.filter((tool) => tool.status === "error").length;
  return {
    label: failing === 0 ? "Healthy" : "Attention",
    sentence:
      failing === 0
        ? `${input.label} is serving this page with every tool check passing.`
        : `${failing} tool ${failing === 1 ? "check is" : "checks are"} failing on ${input.label}.`,
    tone: failing === 0 ? "ready" : "error",
    observedAt: input.inv.fetchedAt,
    source,
    disagreement: null,
  };
}

export function buildServerModel(input: {
  readonly target: DiagnosticsTargetOption | null;
  readonly label: string;
  readonly inventory: RuntimeDiagnosticsResponse | undefined;
  readonly error: string | undefined;
  readonly events: ReadonlyArray<PrototypeEvent>;
  readonly isPlatformAdmin: boolean;
}): DiagnosticsPrototypeModel {
  const inv = input.inventory;
  const facts: PrototypeFact[] = inv
    ? serverFacts(inv)
    : [{ label: "Inventory", value: input.error ?? "not read yet", tone: "muted" }];
  const observedAt = inv?.fetchedAt ?? null;
  return {
    target: input.target,
    verdict: serverVerdict({ label: input.label, inv, error: input.error }),
    groups: [
      {
        id: "server",
        title: "This server",
        source: `Read directly from ${input.label}, ${formatTimestamp(observedAt)}`,
        observedAt,
        facts,
      },
    ],
    checks: facts.map((fact) => ({
      group: "This server",
      name: fact.label,
      result: fact.value,
      tone: fact.tone ?? "muted",
      observedAt,
    })),
    timeline: inv
      ? [
          {
            at: inv.fetchedAt,
            title: "Inventory read",
            detail: `${inv.processes.length} processes, ${inv.tools.length} tools`,
            tone: "ready",
          },
        ]
      : [],
    events: input.events,
    isPlatformAdmin: input.isPlatformAdmin,
  };
}

export function buildDesktopModel(input: {
  readonly target: DiagnosticsTargetOption | null;
  readonly desktop: DesktopDiagnosticsTarget;
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
  readonly events: ReadonlyArray<PrototypeEvent>;
  readonly isPlatformAdmin: boolean;
}): DiagnosticsPrototypeModel {
  const online = input.desktop.presence === "online";
  const facts: PrototypeFact[] = [
    { label: "Registry", value: input.desktop.presence, tone: online ? "ready" : "muted" },
    { label: "Platform", value: input.desktop.platform },
    { label: "Runtime", value: shortId(input.desktop.runtimeId), mono: true },
    { label: "Device", value: String(input.desktop.deviceId), mono: true },
    { label: "Last seen", value: formatTimestamp(input.desktop.observedAt) },
    ...(input.snapshot
      ? [
          { label: "Lifecycle", value: state(input.snapshot.lifecycle.state) },
          { label: "Last failure", value: state(input.snapshot.failure.classification) },
        ]
      : []),
  ];
  return {
    target: input.target,
    verdict: {
      label: online ? "Online" : "Offline",
      sentence: online
        ? `${input.desktop.label} is connected to CMMD. Open Diagnostics on that machine for its processes and logs.`
        : `${input.desktop.label} last checked in ${formatTimestamp(input.desktop.observedAt)}. Forge cannot read it from here.`,
      tone: online ? "ready" : "muted",
      observedAt: input.desktop.observedAt,
      source: "CMMD device registry.",
      disagreement: null,
    },
    groups: [
      {
        id: "desktop",
        title: "Desktop server",
        source: "CMMD device registry",
        observedAt: input.desktop.observedAt,
        facts,
      },
    ],
    checks: facts.map((fact) => ({
      group: "Desktop server",
      name: fact.label,
      result: fact.value,
      tone: fact.tone ?? "muted",
      observedAt: input.desktop.observedAt,
    })),
    timeline: [
      {
        at: input.desktop.observedAt,
        title: online ? "Seen online" : "Last seen",
        detail: `${input.desktop.label} on ${input.desktop.platform}`,
        tone: online ? "ready" : "muted",
      },
    ],
    events: input.events,
    isPlatformAdmin: input.isPlatformAdmin,
  };
}
